import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { Authenticated } from "ra-core";
import { DatePicker } from "@mui/x-date-pickers";
import { CookService } from "../../services/CookServices";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";
import '../OmsTimeTable/index.css';
import { OneMealService } from "../../services/OneMealService";
import { TrialsService } from "../../services/TrialServices";
import UserBundleService from "../../services/BundleService";
import { OneMealServiceStatus } from "../../enums/OneMealServiceStatus";
import { CookReferenceModelName } from "../../enums/CookReferenceModelName";
import { useRecordContext } from "react-admin";
import { retrieveFromLocalStorage, saveOrUpdateLocalStorage } from "../../utils/utils";
import { formatDate, formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import { Calendar, momentLocalizer } from "react-big-calendar";
import CircularLoader from "../Common/CircularLoader";
import ChefForPartyService from "../../services/ChefForPartyService";
import { PartnerTimeSlotStatusEnums } from "../../enums/partnerTimeSlotStatusEnums";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";

moment.locale("en-GB");
const localizer = momentLocalizer(moment);

const cookService = new CookService();
const oneMealService = new OneMealService();
const trialsService = new TrialsService();
const userBundleService = new UserBundleService();
const chefForPartyService = new ChefForPartyService();

const legends = [
  { color: 'darkgrey', label: 'BLOCKED' },
  { color: '#43a047', label: 'SERVICES FULFILLED' },
  { color: '#1d89e4', label: 'COOK_ALLOCATED, COOK_CONFIRMED before SERVICES Start time' },
  { color: '#e53935', label: 'COOK_ALLOCATED, COOK_CONFIRMED after SERVICES Start time' },
  { color: 'black', label: 'PENDING SERVICES' },
  { color: '#FF7722', label: 'PARTY' },
  { color: '#dcfd35 ', label: 'ADMIN_BLOCKED' },
  { color: '#FFA726', label: 'LUNCH' },
  { color: '#b39ddb', label: '4TH_VISIT_BLOCKED' },
  { color: 'pink', label: 'LEAVE' },
];

// Blocks an admin can free by dragging over them on the calendar.
const FREEABLE_BLOCK_TITLES = ['BLOCKED', 'ADMIN_BLOCKED', '4TH_VISIT_BLOCKED'];

interface CookLeaveInterface {
  summary: string,
  id: any,
  start: any,
  startAt: string,
  endAt: string,
  end: any,
  color: any,
  cookId: any,
  referenceId: string,
  referenceModelName: string,
  isLeave: boolean,
  cookDetails: {},
  title: string,
  lastUpdatedBy?: '',
  lastUpdatedByType?: '',
  adminComments?: '',
  cookLeaveId?: any,
  leaveMarked?: any,
}

interface CookLeaveDataInterface {
  [date: string]: CookLeaveInterface[];
}

const CustomToolbar = (props: any) => {
  return (
    <div className="rbc-toolbar">
      <span className="rbc-toolbar-label">{props?.label}</span>
    </div>
  );
};

const CookPartTimeCalendar = (props: any) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [dialog, setDialog] = useState<boolean>(false);
  const [initialDate, setInitialDate] = useState<Date>(moment().toDate());
  const [finalDate, setFinalDate] = useState<Date>(moment(initialDate).add(7, 'days').toDate());
  const [details, setDetails] = useState<any>();
  const [cookOmsMapping, setCookOmsMapping] = useState<any>({});
  const [blockedDialog, setBlockedDialog] = useState<boolean>(false);
  const [cookId, setCookId] = useState<number | null>(null);
  const [hideOneDate, setHideOneDate] = useState<boolean>(false);

  const [slotActionDialog, setSlotActionDialog] = useState<boolean>(false);
  const [slotActionData, setSlotActionData] = useState<any>(null);
  const [slotComment, setSlotComment] = useState<string>('');
  const [slotActionLoading, setSlotActionLoading] = useState<boolean>(false);

  const [mixedSlotDialog, setMixedSlotDialog] = useState<boolean>(false);
  const [mixedSlotMessage, setMixedSlotMessage] = useState<string>('');

  const [lunchDialog, setLunchDialog] = useState<boolean>(false);
  const [lunchDetails, setLunchDetails] = useState<any>(null);
  const [lunchConfirmDialog, setLunchConfirmDialog] = useState<boolean>(false);
  const [lunchRemoveReason, setLunchRemoveReason] = useState<string>('');
  const [lunchRemoveLoading, setLunchRemoveLoading] = useState<boolean>(false);

  const [overrideListDialog, setOverrideListDialog] = useState<boolean>(false);
  const [overrideList, setOverrideList] = useState<any[]>([]);
  const [overrideListLoading, setOverrideListLoading] = useState<boolean>(false);
  const [deletingOverrideId, setDeletingOverrideId] = useState<number | null>(null);
  const [deleteConfirmOverrideId, setDeleteConfirmOverrideId] = useState<number | null>(null);

  const record = useRecordContext();

  const eventStyleGetter = (event: any) => {
    let backgroundColor = event.color || "#3174ad";
    let color = ['#e53935', '#1d89e4', 'black'].includes(event.color) ? 'white' : 'black';
    let border = '1px solid white';
    return { style: { backgroundColor, color, border, boxShadow: '0 -1px 0 0 #ddd' } };
  };

  function mergeContinuousTimeSlots(timeSlots: any[]) {
    if (!timeSlots.length) return [];
    timeSlots.sort(
      (a: { start: Date }, b: { start: Date }) => +new Date(a.start) - +new Date(b.start)
    );
    const mergedSlots = [];
    let currentSlot = timeSlots[0];
    for (let i = 1; i < timeSlots.length; i++) {
      const currentEnd = moment(currentSlot.end);
      const nextStart = moment(timeSlots[i].start);
      const { summary: currentSummary } = currentSlot;
      const { summary: nextSummary } = timeSlots[i];
      if (currentEnd.isSameOrAfter(nextStart) && currentSummary === nextSummary) {
        currentSlot.end = timeSlots[i].end;
      } else {
        currentSlot.start = moment(currentSlot.start).add(0.001, "hours").toDate();
        mergedSlots.push(currentSlot);
        currentSlot = timeSlots[i];
      }
    }
    currentSlot.end = moment(currentSlot.end).subtract(1, "minute").toDate();
    mergedSlots.push(currentSlot);
    return mergedSlots;
  }

  const isLunchSlot = (comment?: string) => {
    return comment?.trim().includes('Auto blocked for lunch');
  };

  const isFourthVisitSlot = (comment?: string) => {
    return comment?.trim().includes('Auto blocked to prevent 4th visit');
  };

  const getTitle = useCallback((eachSlot: any) => {
    if (eachSlot?.referenceModelName) {
      return `${eachSlot.referenceId}_${eachSlot.referenceModelName}`;
    }
    if (eachSlot?.status === "CK_BLOCKED") {
      const comment = eachSlot?.adminComments?.trim();
      if (isLunchSlot(comment)) return 'LUNCH';
      return isFourthVisitSlot(comment) ? '4TH_VISIT_BLOCKED' : 'ADMIN_BLOCKED';
    }
    if (eachSlot?.cookLeaveId || eachSlot?.leaveMarked !== null) {
      return 'LEAVE';
    }
    if (eachSlot?.status === 'BLOCKED') {
      return 'BLOCKED';
    }
    return 'PENDING';
  }, []);

  const getColor = useCallback((eachSlot: any) => {
    if (
      eachSlot?.referenceModelName === CookReferenceModelName.ONE_MEAL_SERVICE ||
      eachSlot?.referenceModelName === CookReferenceModelName.COOK_SUBSCRIPTION_V3 ||
      eachSlot?.referenceModelName === CookReferenceModelName.TRIALS_V3
    ) {
      const currentDate = moment();
      const slotDateTime = moment(
        moment(eachSlot.date).format('YYYY-MM-DD') + "T" + moment(eachSlot.timeSlot, "h:mm A").format("HH:mm")
      );
      if (eachSlot?.serviceStatus === OneMealServiceStatus.FULFILLED) return '#43a047';
      if (
        [OneMealServiceStatus.COOK_ALLOCATED, OneMealServiceStatus.COOK_CONFIRMED].includes(eachSlot?.serviceStatus) &&
        !currentDate.isAfter(slotDateTime)
      ) return '#1d89e4';
      if (
        [OneMealServiceStatus.COOK_ALLOCATED, OneMealServiceStatus.COOK_CONFIRMED].includes(eachSlot?.serviceStatus) &&
        currentDate.isAfter(slotDateTime) &&
        eachSlot?.checkInTime === null
      ) return '#e53935';
      if (eachSlot?.serviceStatus === OneMealServiceStatus.ONGOING && eachSlot?.checkOutTime === null) return '#e53935';
      return "#fdd734";
    }
    if (eachSlot?.referenceModelName === CookReferenceModelName.CHEF_FOR_PARTY) return '#FF7722';
    if (eachSlot?.status === 'CK_BLOCKED' && eachSlot?.referenceModelName === null) {
      if (isLunchSlot(eachSlot?.adminComments)) return '#FFA726';
      return isFourthVisitSlot(eachSlot?.adminComments) ? '#b39ddb' : '#dcfd35';
    }
    if (eachSlot?.cookLeaveId !== null) return 'pink';
    if (eachSlot?.status === 'BLOCKED' && eachSlot?.referenceModelName === null) return '#a9a9a9';
    return 'black';
  }, []);

  const getAllCooks = useCallback(() => {
    let startDate = moment(moment(initialDate).format("YYYY-MM-DD")).format("YYYY-MM-DD");
    let endDate = moment(finalDate).format("YYYY-MM-DD");
    let query: any = { startDate, endDate, cookId: cookId ?? record?.id };
    setLoading(true);
    cookService.getPartTimeCookCalender(query).then((res) => {
      let tempCookOmsMapping: CookLeaveDataInterface = {};
      for (const [key, val] of Object.entries(res)) {
        let tempDetails: any = {};
        const timeSlots: any[] = [];
          (val as any[])?.forEach((eachSlot: any) => {
          let requiredDetails: CookLeaveInterface = {
            summary: "", id: NaN, start: "", end: "", startAt: "", endAt: "",
            color: "", cookId: NaN, referenceId: "", referenceModelName: "",
            isLeave: false, cookDetails: {}, title: '',
            lastUpdatedBy: '', lastUpdatedByType: '', adminComments: ''
          };
          requiredDetails.summary = eachSlot?.referenceModelName
            ? `${eachSlot?.referenceId}_${eachSlot?.referenceModelName}`
            : eachSlot?.status === 'BLOCKED' ? 'BLOCKED' : 'LEAVE';
          requiredDetails.id = eachSlot.id;
          requiredDetails.start = moment(`${eachSlot.date}T${moment(eachSlot.timeSlot, "h:mm A").format("HH:mm")}`).toDate();
          requiredDetails.end = moment(requiredDetails.start).add(0.5, "hours").toDate();
          requiredDetails.startAt = moment(eachSlot.date + "T" + moment(eachSlot.timeSlot, "h:mm A").format("HH:mm")).format();
          requiredDetails.endAt = moment(requiredDetails.startAt).add(0.5, "hours").format();
          requiredDetails.color = getColor(eachSlot);
          requiredDetails.cookId = cookId ?? record?.id;
          requiredDetails.referenceId = eachSlot?.referenceId;
          requiredDetails.referenceModelName = eachSlot?.referenceModelName;
          requiredDetails.cookDetails = record?.cookFullProfile;
          requiredDetails.summary = getTitle(eachSlot);
          requiredDetails.title = getTitle(eachSlot);
          requiredDetails.lastUpdatedBy = eachSlot?.lastUpdatedBy;
          requiredDetails.lastUpdatedByType = eachSlot?.lastUpdatedByType;
          requiredDetails.adminComments = eachSlot?.adminComments;
          requiredDetails.cookLeaveId = eachSlot?.cookLeaveId;
          requiredDetails.leaveMarked = eachSlot?.leaveMarked;
          tempDetails = { ...requiredDetails };
          if (eachSlot?.leaveMarked !== null || eachSlot?.cookLeaveId !== null || (eachSlot?.status === 'BLOCKED' && eachSlot?.referenceModelName === null)) {
            requiredDetails.isLeave = true;
          }
          if (eachSlot?.referenceModelName !== null || eachSlot?.leaveMarked !== null || eachSlot?.cookLeaveId !== null || eachSlot?.status === 'BLOCKED' || eachSlot?.status === 'CK_BLOCKED') {
            timeSlots.push(requiredDetails);
          }
        });
        let mergedSlots = mergeContinuousTimeSlots(timeSlots);
        if (mergedSlots.length === 0) {
          tempDetails.start = '';
          tempDetails.end = '';
          mergedSlots = [tempDetails];
        }
        if (!tempCookOmsMapping[key]) {
          tempCookOmsMapping[key] = [...mergedSlots];
        } else {
          tempCookOmsMapping[key].push(...mergedSlots);
        }
      }
      setCookOmsMapping(tempCookOmsMapping);
      setLoading(false);
    }).catch(() => { setLoading(false); });
  }, [initialDate, finalDate, record, cookId, getColor, getTitle]);

  useEffect(() => {
    const storedData = retrieveFromLocalStorage("cooksCalendar");
    if (storedData) {
      const cooksCalendarDates = JSON.parse(storedData);
      setInitialDate(cooksCalendarDates?.startDate);
      setFinalDate(cooksCalendarDates?.endDate);
    }
  }, []);

  useEffect(() => {
    if (props?.startDate) setInitialDate(props?.startDate);
    if (props?.endDate) setFinalDate(props?.endDate);
    if (props?.cookId) setCookId(props?.cookId);
    setHideOneDate(props?.hideOneDate || false);
  }, [props?.startDate, props?.endDate, props?.cookId, props?.hideOneDate]);

  const generateHalfHourSlots = (start: Date, end: Date): string[] => {
    const slots: string[] = [];
    const startM = moment(start);
    startM.minutes(startM.minutes() < 30 ? 0 : 30).seconds(0).milliseconds(0);
    const endM = moment(end);
    let cur = startM.clone();
    while (cur.isBefore(endM)) {
      slots.push(cur.format('hh:mm A'));
      cur = cur.clone().add(30, 'minutes');
    }
    return slots;
  };

  const handleSlotAction = async () => {
    if (!slotActionData) return;
    setSlotActionLoading(true);
    try {
      const { date, start, end, action, cookId: selectedCookId } = slotActionData;
      const timeSlots = generateHalfHourSlots(start, end);
      for (const timeSlot of timeSlots) {
        await cookService.changeSlotStatus({
          status: action === 'BLOCK' ? PartnerTimeSlotStatusEnums.CK_BLOCKED : PartnerTimeSlotStatusEnums.FREE,
          timeSlot,
          cookId: selectedCookId,
          date,
          comment: slotComment,
        });
      }
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Slot status updated successfully',
        type: CustomSnackbarTypes.SUCCESS,
      });
      setSlotActionDialog(false);
      setSlotComment('');
      const firstRef = Object.values(calendarRefs.current)[0] as any;
      if (firstRef) {
        const scrollable = firstRef.querySelector('.rbc-time-content');
        if (scrollable) savedScrollTopRef.current = scrollable.scrollTop;
      }
      if (calendarContainerRef.current) {
        savedScrollLeftRef.current = calendarContainerRef.current.scrollLeft;
      }
      getAllCooks();
    } catch (_err) {
    } finally {
      setSlotActionLoading(false);
    }
  };

  const onSelectSlot = useCallback((slotInfo: any) => {
    const { start, end } = slotInfo;
    const date = moment(start).format('YYYY-MM-DD');

    const dayEvents: any[] = cookOmsMapping[date] ?? [];

    const overlapping = dayEvents.filter((ev: any) => {
      if (!ev.start || !ev.end) return false;
      return moment(ev.start).isBefore(end) && moment(ev.end).isAfter(start);
    });

    // If all overlapping events are leaves, let onSelectEvent handle the click
    const allLeaves = overlapping.length > 0 && overlapping.every((ev: any) => ev.title === 'LEAVE');
    if (allLeaves) return;

    const hasNonBlockable = overlapping.some(
      (ev: any) => !FREEABLE_BLOCK_TITLES.includes(ev.title)
    );
    if (hasNonBlockable) {
      setMixedSlotMessage(
        'This selection includes slots with active services, party bookings, or leaves that cannot be modified. Please select only free or blocked time slots.'
      );
      setMixedSlotDialog(true);
      return;
    }

    const blockedSlots = overlapping.filter(
      (ev: any) => FREEABLE_BLOCK_TITLES.includes(ev.title)
    );

    if (blockedSlots.length > 0) {

      const selectedSlots = generateHalfHourSlots(start, end);
      const allSlotsBlocked = selectedSlots.every((slotStr) => {
        const slotStart = moment(`${date} ${slotStr}`, 'YYYY-MM-DD hh:mm A');
        return blockedSlots.some((ev: any) => {
          const evStart = moment(ev.start).seconds(0).milliseconds(0);
          return !evStart.isAfter(slotStart) && moment(ev.end).isAfter(slotStart);
        });
      });

      if (!allSlotsBlocked) {
        setMixedSlotMessage(
          'Your selection contains a mix of free and blocked slots. Please select either purely free slots (to block them) or purely blocked slots (to free them).'
        );
        setMixedSlotDialog(true);
        return;
      }
    }

    setSlotActionData({
      date,
      start,
      end,
      action: blockedSlots.length > 0 ? 'FREE' : 'BLOCK',
      cookId: cookId ?? record?.id,
    });
    setSlotComment('');
    setSlotActionDialog(true);
  }, [cookId, record?.id, cookOmsMapping]);

  const fetchOverrideList = async () => {
    const resolvedCookId = cookId ?? record?.id;
    setOverrideListLoading(true);
    try {
      const res = await cookService.getLunchBreakOverrides(Number(resolvedCookId));
      setOverrideList(Array.isArray(res) ? res : []);
    } catch {
      setOverrideList([]);
    } finally {
      setOverrideListLoading(false);
    }
  };

  const handleOpenOverrideList = () => {
    setOverrideListDialog(true);
    fetchOverrideList();
  };

  const handleDeleteOverride = (overrideId: number) => {
    setDeleteConfirmOverrideId(overrideId);
  };

  const confirmAndDelete = async () => {
    if (deleteConfirmOverrideId === null) return;
    const resolvedCookId = cookId ?? record?.id;
    setDeletingOverrideId(deleteConfirmOverrideId);
    setDeleteConfirmOverrideId(null);
    try {
      await cookService.deleteLunchBreakOverride(Number(resolvedCookId), deleteConfirmOverrideId);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Lunch override deleted successfully',
        type: CustomSnackbarTypes.SUCCESS,
      });
      fetchOverrideList();
    } catch {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Failed to delete lunch override',
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setDeletingOverrideId(null);
    }
  };

  const handleLunchRemove = async () => {
    if (!lunchDetails) return;
    setLunchRemoveLoading(true);
    try {
      const date = moment(lunchDetails.start).format('YYYY-MM-DD');
      const resolvedCookId = cookId ?? record?.id;
      await cookService.overrideLunchBreak(Number(resolvedCookId), {
        type: 'DAY_LEVEL',
        date,
        reason: lunchRemoveReason,
      });
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Lunch slot removed successfully',
        type: CustomSnackbarTypes.SUCCESS,
      });
      setLunchConfirmDialog(false);
      setLunchDialog(false);
      setLunchRemoveReason('');
      getAllCooks();
    } catch (_err) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Failed to remove lunch slot',
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setLunchRemoveLoading(false);
    }
  };

  const onEventClick = (data: any) => {
    if (data?.title === 'LEAVE') {
      const leaveId = data?.cookLeaveId || data?.leaveMarked;
      if (leaveId) {
        window.location.href = `/#/CookLeaves/${leaveId}/show`;
      }
      return;
    }
    if (data?.title === 'LUNCH') {
      setLunchDetails(data);
      setLunchDialog(true);
      return;
    }
    setDetails(data);
    if (data?.referenceId) {
      setDialog(true);
    } else if (data?.lastUpdatedBy) {
      setBlockedDialog(true);
    }
  };

  const calendarRefs = useRef<any>({});
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);
  const savedScrollTopRef = useRef<number | null>(null);
  const savedScrollLeftRef = useRef<number | null>(null);

  const syncScroll = (scrollTop: any, sourceKey: any) => {
    Object.entries(calendarRefs.current).forEach(([key, ref]: any) => {
      if (key !== Number(sourceKey) && ref) {
        const scrollableDiv = ref.querySelector('.rbc-time-content');
        if (scrollableDiv && scrollableDiv.scrollTop !== scrollTop) {
          scrollableDiv.scrollTop = scrollTop;
        }
      }
    });
  };

  useEffect(() => {
    const listeners: any[] = [];
    Object.entries(calendarRefs.current).forEach(([key, ref]: any) => {
      if (ref) {
        const scrollableDiv = ref.querySelector('.rbc-time-content');
        if (scrollableDiv) {
          const handleScroll = () => syncScroll(scrollableDiv.scrollTop, key);
          scrollableDiv.addEventListener('scroll', handleScroll);
          listeners.push({ scrollableDiv, handleScroll });
        }
      }
    });

    if (savedScrollTopRef.current !== null || savedScrollLeftRef.current !== null) {
      const savedTop = savedScrollTopRef.current;
      const savedLeft = savedScrollLeftRef.current;
      savedScrollTopRef.current = null;
      savedScrollLeftRef.current = null;
      requestAnimationFrame(() => {
        if (savedTop !== null) {
          Object.values(calendarRefs.current).forEach((ref: any) => {
            if (ref) {
              const scrollable = ref.querySelector('.rbc-time-content');
              if (scrollable) scrollable.scrollTop = savedTop;
            }
          });
        }
        if (savedLeft !== null && calendarContainerRef.current) {
          calendarContainerRef.current.scrollLeft = savedLeft;
        }
      });
    }

    return () => {
      listeners.forEach(({ scrollableDiv, handleScroll }: any) => {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      });
    };
  }, [cookOmsMapping]);

  useEffect(() => {
    const cleanups: (() => void)[] = [];

    Object.values(calendarRefs.current).forEach((ref: any) => {
      if (!ref) return;
      const scrollable = ref.querySelector('.rbc-time-content');
      if (!scrollable) return;

      let isDragging = false;
      let scrollTimer: ReturnType<typeof setInterval> | null = null;

      const stopAutoScroll = () => {
        if (scrollTimer) { clearInterval(scrollTimer); scrollTimer = null; }
      };

      const getAllScrollables = () =>
        Object.values(calendarRefs.current)
          .map((r: any) => r?.querySelector('.rbc-time-content'))
          .filter(Boolean);

      const onMouseDown = () => { isDragging = true; };
      const onMouseUp = () => { isDragging = false; stopAutoScroll(); };

      const onMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        const rect = scrollable.getBoundingClientRect();
        const threshold = 80;
        const speed = 12;
        stopAutoScroll();
        if (e.clientY > rect.bottom - threshold || e.clientY >= window.innerHeight - 20) {
          scrollTimer = setInterval(() => {
            getAllScrollables().forEach((s: any) => { s.scrollTop += speed; });
          }, 16);
        } else if (e.clientY < rect.top + threshold) {
          scrollTimer = setInterval(() => {
            getAllScrollables().forEach((s: any) => { s.scrollTop -= speed; });
          }, 16);
        }
      };

      scrollable.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mouseup', onMouseUp);

      window.addEventListener('mousemove', onMouseMove);

      cleanups.push(() => {
        scrollable.removeEventListener('mousedown', onMouseDown);
        window.removeEventListener('mouseup', onMouseUp);
        window.removeEventListener('mousemove', onMouseMove);
        stopAutoScroll();
      });
    });

    return () => { cleanups.forEach(c => c()); };
  }, [cookOmsMapping]);

  useEffect(() => {
    setCookOmsMapping({});
    getAllCooks();
    const interval = setInterval(() => { getAllCooks(); }, 1000 * 60 * 5);
    return () => { clearInterval(interval); };
  }, [getAllCooks, initialDate, finalDate]);

  const DataContainer = ({ title = '', value = '---' }: { title: string, value: string }) => (
    <div>
      <b style={{ minWidth: "170px", display: "inline-block" }}>{title}</b>
      <span>{value}</span>
    </div>
  );

  const DialogDetails = (props: any) => {
    const cook = props?.data?.cookDetails;
    const [data, setData] = useState<any>(null);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const [redirectLink, setRedirectLink] = useState('');

    useEffect(() => {
      setLoadingData(true);
      if (props?.data?.referenceModelName === 'ONE_MEAL_SERVICE') {
        setRedirectLink(`/#/OneMealService/${props?.data?.referenceId}/show`);
        oneMealService.getOneMealService(props?.data?.referenceId).then((res: any) => {
          setData(res?.data); setLoadingData(false);
        });
      } else if (props?.data?.referenceModelName === 'TRIALS_V3') {
        setRedirectLink(`/#/Trials/${props?.data?.referenceId}/show`);
        trialsService.getTrialsById(props?.data?.referenceId).then((res: any) => {
          setData(res?.data); setLoadingData(false);
        });
      } else if (props?.data?.referenceModelName === 'COOK_SUBSCRIPTION_V3') {
        cookService.getSubscriptionForACook(props?.data?.referenceId).then((d: any) => {
          setRedirectLink(`/#/UserBundles/${d?.userBundleMappingId}/show/7`);
          userBundleService.fetchOneUserBundleMapping(d?.userBundleMappingId).then((res: any) => {
            setData(res?.data); setLoadingData(false);
          });
        });
      } else if (props?.data?.referenceModelName === 'CHEF_FOR_PARTY') {
        setRedirectLink(`/#/ChefForParty/${props?.data?.referenceId}/show`);
        chefForPartyService.fetchOneChefForParty(props?.data?.referenceId).then((res: any) => {
          setData(res?.data); setLoadingData(false);
        });
      }
    }, [props?.data?.referenceId, props?.data?.referenceModelName]);

    return (
      <div>
        <Dialog onClose={props.handleClose} open={props.open} scroll="paper" maxWidth="xl">
          <DialogTitle>
            <b>{props?.data?.referenceModelName}</b>
            <Button variant="contained" sx={{ borderRadius: 30, fontSize: '10px', ml: 2 }} size="small" component={Link} href={redirectLink}>
              View Full Details
            </Button>
          </DialogTitle>
          <DialogContent style={{ display: "flex", flexDirection: "column", width: "400px" }}>
            {!loadingData && props?.data?.referenceModelName === 'ONE_MEAL_SERVICE' &&
              data?.oneMealServiceAmount !== 0 &&
              data?.oneMealServiceAmount === data?.discount && (
                <Box display="flex" justifyContent="center" alignItems="center"
                  sx={{ p: '4px', backgroundColor: '#ffca69', borderRadius: '12px', mb: 1 }}>
                  <Typography fontWeight="600">Pack Visit: 100% Coupon Applied</Typography>
                </Box>
              )}
            <DataContainer title="Status:" value={data?.status} />
            {cook && (
              <div>
                <DataContainer title="Cook Id:" value={cook?.cookId} />
                <DataContainer title="Cook Name:" value={cook?.firstName + " " + cook?.lastName} />
                <DataContainer title="Cook PhoneNo:" value={maskPhoneNumber(data?.cookPhoneNumber)} />
                <DataContainer title="Onboarding Status:" value={cook?.onboardingStatus} />
              </div>
            )}
            <DataContainer title="Customer Name:" value={data?.customerName} />
            <DataContainer title="Customer PhoneNo:" value={data?.customerPhoneNumber} />
            <DataContainer title="City:" value={data?.cityName} />
            <DataContainer title="Area:" value={data?.areaName} />
            <DataContainer title="Locality:" value={data?.localityName} />
            <DataContainer title="Customer Address:" value={data?.house?.address + " " + data?.house?.locality + " " + data?.house?.city} />
            <DataContainer title="Total People:" value={data?.totalPeople} />
            <DataContainer title="Allocator Name:" value={data?.allocatorName} />
            <DataContainer title="Date:" value={formatDate(data?.date)} />
            <DataContainer title="Time:" value={data?.time} />
            <DataContainer title="Check In Time:" value={data?.checkInTime ? formatDateTimeWithSeconds(data?.checkInTime) : 'N/A'} />
            <DataContainer title="Customer Feedback:" value={data?.customerFeedback} />
            <DataContainer title="Check Out Time:" value={data?.checkInTime ? formatDateTimeWithSeconds(data?.checkOutTime) : 'N/A'} />
            <DataContainer title="Customer Comments:" value={data?.customerComments?.[0]?.comment} />
            <DataContainer title="Customer Rating:" value={data?.customerRating} />
          </DialogContent>
          <DialogActions style={{ marginTop: "12px" }}>
            <Button variant="contained" color="error" onClick={() => props.handleClose(false)} size="large">Cancel</Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  };

  const DialogDetailsBlocked = (props: any) => {
    let parsedComments: any[] = [];
    try {
      const raw = props?.data?.adminComments;
      if (raw) {
        const parsed = JSON.parse(raw);
        parsedComments = Array.isArray(parsed) ? parsed : [];
      }
    } catch {
      parsedComments = [];
    }

    return (
      <div>
        <Dialog onClose={props.handleClose} open={props.open} scroll="paper" maxWidth="sm" fullWidth>
          <DialogContent style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Link href={`/#/AdminUsers/${props?.data?.lastUpdatedBy}/show`}>
              <DataContainer title="Blocked By:" value={props?.data?.lastUpdatedBy} />
            </Link>
            <DataContainer title="Blocked By Type:" value={props?.data?.lastUpdatedByType} />
            <DataContainer
              title="Comments:"
              value={parsedComments.length > 0 ? parsedComments.map((c: any) => c?.comment).filter(Boolean).join(', ') : 'No comments'}
            />
          </DialogContent>
          <DialogActions style={{ marginTop: "12px" }}>
            <Button variant="contained" color="error" onClick={() => props.handleClose(false)} size="large">Cancel</Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  };

  return (
    <Authenticated>
      <>
        <DialogDetails open={dialog} handleClose={() => setDialog(false)} data={details} />
        <DialogDetailsBlocked open={blockedDialog} handleClose={() => setBlockedDialog(false)} data={details} />

        {/* Lunch slot detail popup */}
        <Dialog open={lunchDialog} onClose={() => setLunchDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>🍽️ Lunch Slot</DialogTitle>
          <DialogContent>
            <Box sx={{ background: '#fff8e1', borderRadius: 2, p: 1.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography fontWeight={600} fontSize="0.9rem">
                {lunchDetails ? moment(lunchDetails.start).format('DD MMM YYYY') : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Timing</Typography>
              <Typography fontWeight={600} fontSize="0.9rem">
                {lunchDetails
                  ? `${moment(lunchDetails.start).format('h:mm A')} – ${moment(lunchDetails.end).format('h:mm A')}`
                  : ''}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setLunchDialog(false)} color="inherit" variant="outlined" size="small">
              Close
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => setLunchConfirmDialog(true)}
            >
              Remove Lunch Slot
            </Button>
          </DialogActions>
        </Dialog>

        {/* Lunch slot removal confirmation */}
        <Dialog open={lunchConfirmDialog} onClose={() => { if (!lunchRemoveLoading) setLunchConfirmDialog(false); }} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Confirm Removal</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Are you sure you want to remove the lunch slot on{' '}
              <strong>{lunchDetails ? moment(lunchDetails.start).format('DD MMM YYYY') : ''}</strong>?
            </Typography>
            <TextField
              label="Reason"
              value={lunchRemoveReason}
              onChange={(e) => setLunchRemoveReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter reason for removal..."
              size="small"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setLunchConfirmDialog(false)}
              disabled={lunchRemoveLoading}
              color="inherit"
              variant="outlined"
              size="small"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleLunchRemove}
              disabled={lunchRemoveLoading || !lunchRemoveReason.trim()}
              startIcon={lunchRemoveLoading ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {lunchRemoveLoading ? 'Removing…' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Lunch overrides list dialog */}
        <Dialog
          open={overrideListDialog}
          onClose={() => setOverrideListDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 0, mb: 2 }}>Lunch Break Overrides</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <CommonListTable
              title=""
              columns={[
                { label: 'ID', field: 'id' },
                {
                  label: 'Date',
                  render: (row: any) => moment(row.date).format('DD MMM YYYY'),
                },
                {
                  label: 'Created At',
                  render: (row: any) => moment(row.createdAt).format('DD MMM YYYY hh:mm A'),
                  nowrap: true,
                },
                { label: 'Type', field: 'type' },
                { label: 'Created By Type', field: 'createdByType' },
                {
                  label: 'Created By Id',
                  stopRowClick: true,
                  render: (row: any) => {
                    if (row.createdBy === null || row.createdBy === undefined) return '-';
                    // Admins link to their detail page; cooks are shown as plain text
                    return row.createdByType === 'ADMIN' ? (
                      <Link
                        href={`/#/AdminUsers/${row.createdBy}/show`}
                        sx={{ color: '#1976d2', cursor: 'pointer', fontWeight: 500 }}
                      >
                        {row.createdBy}
                      </Link>
                    ) : (
                      <span>{row.createdBy}</span>
                    );
                  },
                },
                {
                  label: 'Reason',
                  render: (row: any) => (
                    <Typography
                      variant="body2"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        maxWidth: 260,
                      }}
                    >
                      {row.reason || '-'}
                    </Typography>
                  ),
                },
                {
                  label: 'Actions',
                  stopRowClick: true,
                  render: (row: any) => (
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      disabled={deletingOverrideId === row.id}
                      onClick={() => handleDeleteOverride(row.id)}
                      startIcon={deletingOverrideId === row.id ? <CircularProgress size={12} color="inherit" /> : null}
                      sx={{ textTransform: 'none', minWidth: 80 }}
                    >
                      {deletingOverrideId === row.id ? 'Deleting…' : 'Delete'}
                    </Button>
                  ),
                },
              ] as CommonListColumn[]}
              data={overrideList}
              loading={overrideListLoading}
              emptyText="No lunch overrides found."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOverrideListDialog(false)} color="inherit" variant="outlined" size="small">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteConfirmOverrideId !== null}
          onClose={() => setDeleteConfirmOverrideId(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Are you sure you want to delete this lunch override? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setDeleteConfirmOverrideId(null)}
              color="inherit"
              variant="outlined"
              size="small"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={confirmAndDelete}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={mixedSlotDialog} onClose={() => setMixedSlotDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Invalid Slot Selection</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {mixedSlotMessage}
            </Typography>
            <Box mt={2} display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                • Select <strong>only free slots</strong> to block them.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                • Select <strong>only blocked slots</strong> to free them.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="contained" onClick={() => setMixedSlotDialog(false)} size="small">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={slotActionDialog}
          onClose={() => { if (!slotActionLoading) setSlotActionDialog(false); }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            {slotActionData?.action === 'BLOCK' ? '🔒 Block Time Slot' : '🔓 Free Time Slot'}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={0.5}>
              <Box sx={{ background: '#f5f5f5', borderRadius: 2, p: 1.5 }}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography fontWeight={600} fontSize="0.9rem">{slotActionData?.date}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" color="text.secondary">Time Range</Typography>
                    <Typography fontWeight={600} fontSize="0.9rem">
                      {moment(slotActionData?.start).format('h:mm A')} – {moment(slotActionData?.end).format('h:mm A')}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Slots to {slotActionData?.action === 'BLOCK' ? 'block' : 'free'}:&nbsp;
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {slotActionData
                    ? generateHalfHourSlots(slotActionData.start, slotActionData.end).join(' · ')
                    : ''}
                </Typography>
              </Box>
              <TextField
                label="Reason / Comment"
                value={slotComment}
                onChange={(e) => setSlotComment(e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="Enter reason..."
                size="small"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setSlotActionDialog(false)}
              disabled={slotActionLoading}
              color="inherit"
              variant="outlined"
              size="small"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color={slotActionData?.action === 'BLOCK' ? 'error' : 'success'}
              onClick={handleSlotAction}
              disabled={slotActionLoading}
              size="small"
              startIcon={slotActionLoading ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {slotActionLoading
                ? 'Processing…'
                : slotActionData?.action === 'BLOCK'
                  ? 'Confirm Block'
                  : 'Confirm Free'}
            </Button>
          </DialogActions>
        </Dialog>

        <div style={{ display: "flex", flexDirection: "column", height: "200%", minWidth: "0", overflowX: "scroll" }}>
          <Box display="flex" gap="20px" mb={1} alignItems="center">
            {hideOneDate ? <></> : (
              <>
                <DatePicker
                  mask=""
                  label="Select Start Date"
                  inputFormat="dd MMM yyyy"
                  value={initialDate}
                  onChange={(newValue: any) => { setInitialDate(newValue); }}
                  renderInput={(params) => <TextField {...params} />}
                  onAccept={(newValue: any) => {
                    saveOrUpdateLocalStorage("cooksCalendar", { startDate: moment(newValue) });
                  }}
                />
                <DatePicker
                  mask=""
                  label="Select End Date"
                  inputFormat="dd MMM yyyy"
                  value={finalDate}
                  onChange={(newValue: any) => { setFinalDate(newValue); }}
                  renderInput={(params) => <TextField {...params} />}
                  onAccept={(newValue: any) => {
                    saveOrUpdateLocalStorage("cooksCalendar", { endDate: moment(newValue) });
                  }}
                />
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={handleOpenOverrideList}
                  sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600, color: '#fff', ml: 'auto' }}
                >
                  Manage Lunch Overrides
                </Button>
              </>
            )}
          </Box>

          {!hideOneDate && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, ml: 0.5 }}>
              💡 Click or drag on empty slots to block · Drag over a BLOCKED / ADMIN_BLOCKED / 4TH_VISIT_BLOCKED area to free only those slots
            </Typography>
          )}

          {loading
            ? (
              <Box display="flex" justifyContent="center" alignItems="center" width="100vw">
                <CircularLoader />
              </Box>
            )
            : (
              <div ref={calendarContainerRef} style={{ display: "flex", height: "100%", overflow: "scroll" }}>
                {Object.keys(cookOmsMapping).length !== 0 &&
                  Object.entries(cookOmsMapping).map(([key, value]: any) => (
                    <div
                      style={{ textAlign: "center", width: "200px" }}
                      key={key}
                      ref={(el) => (calendarRefs.current[key] = el)}
                      className="first-calendar"
                    >
                      <Calendar
                        localizer={localizer}
                        events={value}
                        defaultView="day"
                        views={["day"]}
                        style={{ height: "100%", backgroundColor: "white" }}
                        components={{ toolbar: CustomToolbar }}
                        step={30}
                        timeslots={1}
                        date={moment(key).format("YYYY-MM-DD")}
                        selectable
                        onSelectSlot={onSelectSlot}
                        onSelectEvent={onEventClick}
                        eventPropGetter={eventStyleGetter}
                        min={new Date(2024, 0, 1, 5, 0)}
                        max={new Date(2024, 0, 1, 23, 59)}
                      />
                    </div>
                  ))}
              </div>
            )}

          {!hideOneDate && (
            <Box display="flex" flexDirection="row" flexWrap="wrap">
              {[
                [legends[0], legends[1]],
                [legends[2], legends[3]],
                [legends[4], legends[5]],
                [legends[6], legends[7]],
                [legends[8]],
              ].map((group, gi) => (
                <Box key={gi} m={2} display="flex" flexDirection="column">
                  {group.map((eachLegend) => (
                    <Box key={eachLegend.label} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 20, height: 20, backgroundColor: eachLegend.color, borderRadius: 1 }} m={0.5} mr={1} />
                      <Typography fontSize={12}>{eachLegend.label}</Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </div>
      </>
    </Authenticated>
  );
};

export default CookPartTimeCalendar;
